/**
 * Effective Space notification settings for the current alias.
 *
 * Reads the alias-wide defaults and per-Space overrides from
 * `SpacePreferencesProvider` and resolves channel > category chain > Space >
 * default. Outside the provider (public shell) everything resolves to the
 * built-in defaults and saving is unavailable.
 */

import {
  DEFAULT_SPACE_NOTIFICATION_DEFAULTS,
  resolveSpaceNotifyLevel,
  resolveSpaceToggle,
  type ResolvedSpaceNotifyLevel,
  type SpaceNotificationDefaults,
  type SpaceNotificationOverrides,
  type SpaceNotificationOverridesPatch,
  type SpaceNotifyToggle,
} from '@adieuu/shared';
import { useCallback, useMemo } from 'react';
import { useOptionalSpacePreferences } from './useSpacePreferences';

export interface ResolveSpaceNotifyLevelOptions {
  channelId?: string;
  /** Nearest-first category chain; omit `channelId` to get what a child of `categoryPath[0]` inherits. */
  categoryPath?: readonly string[];
}

export interface SpaceNotificationSettings {
  /** False outside the authenticated shell; setters are no-ops then. */
  available: boolean;
  defaults: SpaceNotificationDefaults;
  overridesFor: (spaceId: string) => SpaceNotificationOverrides | undefined;
  resolveLevel: (spaceId: string, options?: ResolveSpaceNotifyLevelOptions) => ResolvedSpaceNotifyLevel;
  /** Effective on/off alert (announcements, Space changes) for a Space. */
  resolveToggle: (spaceId: string, toggle: SpaceNotifyToggle) => boolean;
  resolveAnnouncements: (spaceId: string) => boolean;
  setSpaceNotifications: (spaceId: string, patch: SpaceNotificationOverridesPatch) => Promise<void>;
  setNotificationDefaults: (defaults: SpaceNotificationDefaults) => Promise<void>;
}

const noop = async () => {};

export function useSpaceNotificationSettings(): SpaceNotificationSettings {
  const prefs = useOptionalSpacePreferences();
  const preferences = prefs?.preferences;
  const defaults = prefs?.notificationDefaults ?? DEFAULT_SPACE_NOTIFICATION_DEFAULTS;

  const overridesFor = useCallback(
    (spaceId: string) => preferences?.[spaceId]?.notifications,
    [preferences],
  );

  const resolveLevel = useCallback(
    (spaceId: string, options?: ResolveSpaceNotifyLevelOptions) =>
      resolveSpaceNotifyLevel({
        defaults,
        overrides: preferences?.[spaceId]?.notifications,
        channelId: options?.channelId,
        categoryPath: options?.categoryPath,
      }),
    [defaults, preferences],
  );

  const resolveToggle = useCallback(
    (spaceId: string, toggle: SpaceNotifyToggle) =>
      resolveSpaceToggle(toggle, defaults, preferences?.[spaceId]?.notifications),
    [defaults, preferences],
  );

  const resolveAnnouncements = useCallback(
    (spaceId: string) => resolveToggle(spaceId, 'announcements'),
    [resolveToggle],
  );

  return useMemo(
    () => ({
      available: prefs !== null,
      defaults,
      overridesFor,
      resolveLevel,
      resolveToggle,
      resolveAnnouncements,
      setSpaceNotifications: prefs?.setSpaceNotifications ?? noop,
      setNotificationDefaults: prefs?.setNotificationDefaults ?? noop,
    }),
    [prefs, defaults, overridesFor, resolveLevel, resolveToggle, resolveAnnouncements],
  );
}
